import { useMemo } from 'react';
import { Button, Dropdown, InlineNotification } from '@carbon/react';
import { Location } from '@carbon/react/icons';
import { format0, formatNumber } from 'utils/number';
import {
  END_A,
  END_B,
  INCONSISTENT_BOTTOM_HOLE_MESSAGE,
  isBottomEndValid,
  type LtdCandidate,
  type LtdEndKey,
  type LtdEndRef,
} from './composite-geometry';
import type { WellborePreview } from '../../hooks';
import styles from './add-wellbore-tearsheet.module.scss';

/** One selectable endpoint across the whole selection. */
interface EndOption {
  id: string;
  label: string;
  ref: LtdEndRef;
  /**
   * Whether the chain accepts this end for the role. Only the bottom hole is ever false —
   * a surface end is not invalid in itself. Carbon's Dropdown reads `disabled` off the item
   * to grey it and keep it unselectable.
   */
  valid: boolean;
  disabled?: boolean;
}

/**
 * One option's stable id: a stick and which of its two ends.
 * @param {LtdEndRef} ref The stick and end.
 * @return {string} The id.
 */
const optionId = (ref: LtdEndRef) => `${ref.slotId}:${ref.end}`;

// Five decimals of degree is about a metre — enough to tell two ends of one stick apart
// without printing precision the survey does not carry. No commas: these are coordinates.
/**
 * One end's position, as the dropdown prints it.
 * @param {number} lon Longitude.
 * @param {number} lat Latitude.
 * @return {string} "lat, lon" to five decimals.
 */
const coordinate = (lon: number, lat: number) => {
  /**
   * @param {number} value The degree value.
   * @return {string} It, to five decimals.
   */
  const degrees = (value: number) =>
    formatNumber(value, 5, 5, 'decimal', false);
  return `${degrees(lat)}, ${degrees(lon)}`;
};

/**
 * Every end of every selected stick, as the two dropdowns list them.
 * @param {LtdCandidate[]} selected The checked sticks.
 * @return {EndOption[]} The options, stick order then end order.
 */
const endOptions = (selected: LtdCandidate[]): EndOption[] =>
  selected.flatMap((candidate) =>
    ([END_A, END_B] as LtdEndKey[]).flatMap((end) => {
      const served = candidate.ends?.find(
        (candidateEnd) => candidateEnd.id === end
      );
      if (!served) return [];

      const ref: LtdEndRef = { slotId: candidate.ltdPropertyId, end };
      return [
        {
          id: optionId(ref),
          label: `LTD-${candidate.ltdPropertyId} · ${
            served.label
          } — ${coordinate(served.lon, served.lat)}`,
          ref,
          valid: true,
        },
      ];
    })
  );

/**
 * An item as the bottom-hole list shows it: an invalid end is greyed out and unselectable.
 * @param {EndOption} option The end.
 * @param {Map<number, LtdEndKey> | null} validEnds Valid bottom end per stick, or null if unknown.
 * @return {EndOption} The end, flagged.
 */
const flagBottom = (
  option: EndOption,
  validEnds: Map<number, LtdEndKey> | null
): EndOption => {
  const valid = isBottomEndValid(validEnds, option.ref);
  return { ...option, valid, disabled: !valid };
};

/**
 * A bottom-hole item's content: the invalid end stays visible, with the reason attached.
 * @param {EndOption | null} item The end.
 * @return {ReactNode} Its label, plus the reason when invalid.
 */
const renderItem = (item: EndOption | null) => {
  if (!item) return '';
  if (item.valid) return item.label;
  return (
    <span title={INCONSISTENT_BOTTOM_HOLE_MESSAGE}>
      {item.label}
      <span className={styles.invalidEndNote}>
        {' '}
        — inconsistent with the chained order
      </span>
    </span>
  );
};

interface WellboreEndDesignationProps {
  selected: LtdCandidate[];
  surface: LtdEndRef | null;
  bottom: LtdEndRef | null;
  onDesignate: (role: 'surface' | 'bottom', ref: LtdEndRef | null) => void;
  /** Which role the next map click sets; null when neither is armed. */
  pickRole: 'surface' | 'bottom' | null;
  onArm: (role: 'surface' | 'bottom' | null) => void;
  /** The backend's dry run of the current inputs. */
  preview: WellborePreview;
  /** Valid bottom end per stick for the current surface hole; null while unknown. */
  validBottomEnds?: Map<number, LtdEndKey> | null;
  /** The reader's bottom pick was cleared because it stopped being valid. */
  bottomReset?: boolean;
  /** Why the inputs are not yet worth sending — a local input check, not geometry. */
  refusal: string | null;
  disabled?: boolean;
}

/**
 * The surface / bottom hole designations and the previewed bore's summary.
 *
 * Both designations are REQUIRED, for a single stick as much as a composite (PRD 6.3) — the
 * backend refuses without them, so there is nothing to preview until both are set.
 *
 * Each dropdown is paired with a location button that arms map-click picking for that role;
 * the click that uses it disarms it, as does arming the other role or picking from the list.
 * @param {WellboreEndDesignationProps} props The selection, the designations, the preview and the handlers.
 * @return {JSX.Element} The controls.
 */
export function WellboreEndDesignation({
  selected,
  surface,
  bottom,
  onDesignate,
  pickRole,
  onArm,
  preview,
  validBottomEnds = null,
  bottomReset = false,
  refusal,
  disabled = false,
}: WellboreEndDesignationProps) {
  const options = useMemo(() => endOptions(selected), [selected]);

  /**
   * The option matching a designation, for the dropdown's controlled value.
   * @param {EndOption[]} items The options that dropdown is showing.
   * @param {LtdEndRef | null} ref The designation, or null.
   * @return {EndOption | null} The matching option, or null.
   */
  const chosenFrom = (items: EndOption[], ref: LtdEndRef | null) => {
    return ref
      ? (items.find((item) => item.id === optionId(ref)) ?? null)
      : null;
  };

  // Pin first, then the field — the mock puts the map-pick control on the LEAD edge of
  // each designation rather than trailing it.
  /* One lateral has exactly one other end, so the bottom hole is not a question: it
     follows the surface. The control is shown but locked, with the reason under it, rather
     than hidden — the bore still HAS a bottom hole and the reader needs to see which. */
  const singleLateral = selected.length === 1;

  // The bottom hole cannot share the surface hole's lateral once the composite has more
  // than one member — the chain could never pass through the others — so those endpoints
  // are not offered at all.
  const bottomOptions = useMemo(() => {
    const surfaceId = surface ? optionId(surface) : null;
    const offered =
      selected.length > 1
        ? options.filter((option) => option.ref.slotId !== surface?.slotId)
        : options.filter((option) => option.id !== surfaceId);
    // Flag each end against the chain's accepted bottom end. Greyed, not hidden, so the
    // reader sees the end exists and why it cannot be the bottom.
    return offered.map((option) => flagBottom(option, validBottomEnds));
  }, [options, selected.length, surface, validBottomEnds]);

  // Pin first, then the field — the mock puts the map-pick control on the LEAD edge of
  // each designation rather than trailing it.
  /**
   * One designation row: its map-pick button and its dropdown.
   * @param {'surface' | 'bottom'} role Which designation the row sets.
   * @param {LtdEndRef | null} ref What it is currently set to.
   * @return {JSX.Element} The row.
   */
  const row = (role: 'surface' | 'bottom', ref: LtdEndRef | null) => {
    const locked = role === 'bottom' && singleLateral;
    const items = role === 'bottom' ? bottomOptions : options;

    return (
      <div className={styles.designationRow}>
        <Button
          kind={pickRole === role ? 'primary' : 'tertiary'}
          size="md"
          hasIconOnly
          renderIcon={Location}
          iconDescription={
            pickRole === role
              ? 'Click an endpoint on the map to set it — click again to cancel'
              : `Pick the ${role} hole on the map`
          }
          tooltipPosition="bottom"
          disabled={disabled || locked || options.length === 0}
          onClick={() => onArm(pickRole === role ? null : role)}
        />
        <Dropdown
          id={`wellbore-${role}-hole`}
          className={styles.designationDropdown}
          titleText={role === 'surface' ? 'Surface hole' : 'Bottom hole'}
          label="Select an end"
          items={items}
          selectedItem={chosenFrom(items, ref)}
          itemToString={(item: EndOption | null) => item?.label ?? ''}
          itemToElement={role === 'bottom' ? renderItem : undefined}
          disabled={disabled || locked || items.length === 0}
          helperText={
            locked ? 'The other end of the single lateral.' : undefined
          }
          onChange={({ selectedItem }: { selectedItem: EndOption | null }) => {
            // Picking from the list is an answer, so it disarms the map.
            onArm(null);
            onDesignate(role, selectedItem?.ref ?? null);
          }}
        />
      </div>
    );
  };

  const members = preview.orderedMembers;

  return (
    <div className={styles.designations}>
      {/* Surface and bottom sit SIDE BY SIDE above the map, as the mock draws them —
          they are one decision about the same bore, not a stacked form. Everything
          below (refusals, the chain summary, advisories) spans the pair. */}
      <div className={styles.designationPair}>
        {row('surface', surface)}
        {row('bottom', bottom)}
      </div>

      {/* The server's own refusals — why these sticks will not chain. Distinct from the
          local input check below, which only knows that something is unset. */}
      {preview.errors.map((message) => (
        <InlineNotification
          key={message}
          kind="error"
          lowContrast
          hideCloseButton
          title="These LTDs cannot build a wellbore"
          subtitle={message}
          className={styles.notice}
        />
      ))}

      {bottomReset && (
        <p className={styles.chainRefusal} role="status">
          Bottom hole reset — no longer valid for this order.
        </p>
      )}

      {/* The map is waiting for a click — say which role it will take. */}
      {pickRole && (
        <p className={styles.pickHint}>
          Click an endpoint on the map to set the{' '}
          {pickRole === 'surface' ? 'surface hole' : 'bottom hole'}. Press the
          button again to cancel.
        </p>
      )}

      {refusal && <p className={styles.chainRefusal}>{refusal}</p>}

      {preview.loading && (
        <p className={styles.chainRefusal}>Deriving the wellbore…</p>
      )}

      {members.length > 0 && (
        <>
          {/* Built from what the backend says it TRAVERSED, start to end — so a composite
              names both the surface and the bottom stick rather than trailing off, and an
              untraversed stick never appears. */}
          <p className={styles.chainSummary}>
            Surface <strong>LTD-{members[0]}</strong>
            {members.slice(1).map((slotId) => (
              <span key={slotId}>
                {' → '}
                <strong>LTD-{slotId}</strong>
              </span>
            ))}{' '}
            Bottom hole
            {preview.perforatedLateralLengthFt !== null && (
              <>
                {' '}
                · {format0(preview.perforatedLateralLengthFt)} ft total lateral
              </>
            )}
            {/* The count is what makes a composite read as one; a single stick is
                already named above it, so saying "· 1 LTD" only adds noise. */}
            {members.length > 1 && <> · {members.length} LTDs</>}
          </p>
        </>
      )}
    </div>
  );
}
